import type { SessionBase } from "./params";

/**
 * Per-child "already read" marks for the library shelf.
 *
 * A tiny localStorage set of the storyIds this child has finished, used only to
 * place a soft star on a book they've read before — a gentle "you've been here"
 * for the parent, never a score or a lock. It is presentational and private to
 * the device; it never gates access to a story and never leaves the browser.
 *
 * Distinct from the keepsake night sky (NightSky), which counts NIGHTS of
 * stories (once per session); this tracks WHICH stories have ever been finished.
 * Never throws — if storage is unavailable we simply show no read marks.
 */

const KEY_PREFIX = "tella:read:";

function keyFor(base: SessionBase | null): string | null {
  if (!base?.family || !base?.child) return null;
  return `${KEY_PREFIX}${base.family}:${base.child}`.toLowerCase();
}

/** The set of storyIds this child has finished before (empty if none/unknown). */
export function getReadStoryIds(base: SessionBase | null): Set<string> {
  const key = keyFor(base);
  if (!key || typeof localStorage === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr)
      ? new Set(arr.filter((s): s is string => typeof s === "string"))
      : new Set();
  } catch {
    return new Set();
  }
}

/** Mark one story finished. Idempotent; never throws. */
export function markStoryRead(base: SessionBase | null, storyId: string): void {
  const key = keyFor(base);
  if (!key || !storyId || typeof localStorage === "undefined") return;
  try {
    const set = getReadStoryIds(base);
    if (set.has(storyId)) return;
    set.add(storyId);
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    /* storage unavailable — read marks are a nicety, not a requirement */
  }
}
